"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Mail, Phone, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "100% Direct Access",
  description: "You work directly with me, Yassine Essaadi. No account managers, middle management, or junior hand-offs."
}, {
  id: 2,
  title: "Tailored SEO Roadmap",
  description: "Custom strategic plan designed around your business model, search landscape, and genuine growth goals."
}, {
  id: 3,
  title: "Transparent & Honest",
  description: "Practical execution, clear reporting, and realistic timelines with zero buzzwords or fake agency claims."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-secondary/60 blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 -z-10 size-80 rounded-full bg-accent/15 blur-3xl opacity-60 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium bg-secondary text-secondary-foreground rounded-full flex items-center gap-1.5 border border-border/50">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct Expert Partnership</span>
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif leading-tight">
              Ready to Grow Your Organic Traffic with a Personal SEO Specialist?
            </motion.h2>

            <motion.p variants={itemVariants} className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
              When you book a consultation, you speak directly with me — Yassine Essaadi.
              I will analyze your current search position, outline clear opportunities for sustainable growth,
              and show you exactly how my hands-on strategy gets real results.
            </motion.p>

            {}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="bg-card border border-border/80 rounded-xl p-4 flex flex-col gap-2 transition-[border-color,box-shadow] hover:border-accent/40 hover:shadow-sm">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{benefit.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {benefit.description}
                  </p>
                </div>)}
            </motion.div>

            {}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-6 pt-2 text-xs text-muted-foreground border-t border-border/60">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>No long-term lock-in contracts</span>
              </div>
              <div className="flex items-center gap-2">
                <UserCheck className="size-4 text-primary" />
                <span>30-minute tailored discussion</span>
              </div>
            </motion.div>
          </div>

          {}
          <div className="lg:col-span-5">
            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border/80 shadow-lg rounded-2xl overflow-hidden relative">
                <div className="bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="size-12 rounded-full overflow-hidden border-2 border-primary-foreground/30 shrink-0 bg-muted">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg font-serif">
                        Yassine Essaadi
                      </h3>
                      <p className="text-xs text-primary-foreground/80 font-sans">
                        Independent SEO Consultant
                      </p>
                    </div>
                  </div>
                  <Separator className="bg-primary-foreground/20 my-1" />
                  <p className="text-sm text-primary-foreground/90 leading-relaxed">
                    {"\"I personally review every consultation request. Let's discuss your current search performance and how we can achieve sustainable rankings.\""}
                  </p>
                </div>

                <CardContent className="p-6 sm:p-8 flex flex-col gap-6">
                  <div className="flex flex-col gap-3">
                    <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium text-base h-12 rounded-lg flex items-center justify-center gap-2 group transition-[background-color,box-shadow]">
                      <Link to="/ContactPage">
                        <SafeHydrate><Calendar className="size-5 shrink-0" /></SafeHydrate>
                        <span>Book a Free SEO Consultation</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>

                    <Button asChild variant="outline" size="lg" className="w-full border-border hover:bg-muted text-foreground font-medium text-sm h-11 rounded-lg flex items-center justify-center gap-2">
                      <Link to="/ContactPage">
                        <span>Get a Free Local SEO Audit</span>
                      </Link>
                    </Button>
                  </div>

                  <p className="text-xs text-center text-muted-foreground leading-normal">
                    Free 30-minute video or phone call. No hard sales pressure.
                  </p>

                  <Separator className="bg-border" />

                  {}
                  <div className="flex flex-col gap-2.5 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      Direct Contact Details:
                    </span>
                    <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                      <Mail className="size-3.5 text-primary shrink-0" />
                      <span>contact@ye-consultancy.com</span>
                    </a>
                    <a href="tel:+447490984430" className="flex items-center gap-2 hover:text-primary transition-colors">
                      <Phone className="size-3.5 text-primary shrink-0" />
                      <span>+44 7490984430</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}